/**
 * Keyboard — native keyboard handling for Capacitor WebView.
 *
 * Prevents the common issue where the mobile keyboard covers
 * form inputs. Also provides programmatic keyboard control.
 *
 * Works on both iOS and Android.
 */

import { isNativePlatform } from '@/lib/utils/platform'

type KeyboardListener = { remove: () => void }

/**
 * Elements that should NOT trigger keyboard dismiss when tapped.
 * Matches: form inputs, interactive controls, and container widgets
 * like comboboxes / listboxes where tapping an option shouldn't
 * dismiss the keyboard before the selection registers.
 */
const INTERACTIVE_SELECTOR = [
  'input',
  'textarea',
  'select',
  'button',
  'a',
  'label',
  '[contenteditable]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[role="option"]',
  '[role="dialog"]',
  '[role="menu"]',
  '[role="menuitem"]',
  '[data-vaul-drawer]',
  '[data-vaul-drawer-overlay]',
  '[data-radix-popper-content-wrapper]',
  '.PhoneInputCountrySelect',
  '.PhoneInputCountrySelectArrow',
].join(',')

/**
 * Check if a touch target (or any of its ancestors) is an interactive
 * element that should keep the keyboard open.
 */
function isInteractiveElement(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  // Direct match or ancestor match
  return target.closest(INTERACTIVE_SELECTOR) !== null
}

/**
 * Initialize keyboard handling for the native app.
 *
 * - Automatically scrolls focused inputs into view when the keyboard opens
 * - Adds tap-to-dismiss: tapping non-interactive areas hides the keyboard
 * - Enables the iOS accessory bar (Done / Prev / Next)
 *
 * Call once from a root client component (NativeAppShell).
 * @returns Cleanup function to remove listeners
 */
export async function initKeyboardHandling(): Promise<() => void> {
  if (!isNativePlatform()) return () => {}

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    const listeners: KeyboardListener[] = []
    const cleanups: (() => void)[] = []

    // ── 1. Enable iOS accessory bar (Done / Prev / Next) ──────────
    // By default Capacitor hides it. The "Done" button is the #1
    // iOS mechanism for dismissing a keyboard.
    await Keyboard.setAccessoryBarVisible({ isVisible: true })

    // ── 2. Auto-scroll focused input into view on keyboard show ───
    const showListener = await Keyboard.addListener('keyboardWillShow', (info) => {
      const activeEl = document.activeElement as HTMLElement | null
      if (!activeEl) return

      // Calculate if the element is hidden behind the keyboard
      const rect = activeEl.getBoundingClientRect()
      const visibleHeight = window.innerHeight - info.keyboardHeight
      const buffer = 20 // px above the element

      if (rect.bottom > visibleHeight) {
        // scrollIntoView with 'center' works best across iOS/Android
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      } else if (rect.top < 0) {
        // Element is above viewport (e.g. after scroll correction)
        activeEl.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }

      // Ensure window also adjusts if scrollIntoView doesn't suffice
      requestAnimationFrame(() => {
        const newRect = activeEl.getBoundingClientRect()
        const newVisibleHeight = window.innerHeight - info.keyboardHeight
        if (newRect.bottom > newVisibleHeight) {
          const scrollAmount = newRect.bottom - newVisibleHeight + buffer
          window.scrollBy({ top: scrollAmount, behavior: 'smooth' })
        }
      })
    })
    listeners.push(showListener)

    // ── 3. Keyboard did hide ──────────────────────────────────────
    const hideListener = await Keyboard.addListener('keyboardWillHide', () => {
      // No manual cleanup needed — KeyboardResize.Body handles layout
      // restoration automatically. We keep this listener for future
      // extensions (e.g. analytics, scroll restoration).
    })
    listeners.push(hideListener)

    // ── 4. Tap-to-dismiss: blur active element on non-interactive tap ──
    const handleTouchStart = (e: TouchEvent) => {
      // Only dismiss if there's an active focusable element (keyboard is likely open)
      const activeEl = document.activeElement
      if (
        !activeEl ||
        (activeEl.tagName !== 'INPUT' &&
          activeEl.tagName !== 'TEXTAREA' &&
          !(activeEl as HTMLElement).isContentEditable)
      ) {
        return
      }

      // Don't dismiss if the tap target is interactive
      if (isInteractiveElement(e.target)) return // Blur the active element to dismiss keyboard
      ;(activeEl as HTMLElement).blur()
    }

    document.addEventListener('touchstart', handleTouchStart, { passive: true })
    cleanups.push(() => document.removeEventListener('touchstart', handleTouchStart))

    return () => {
      listeners.forEach((l) => l.remove())
      cleanups.forEach((fn) => fn())
    }
  } catch {
    return () => {}
  }
}

/**
 * Programmatically hide the keyboard.
 * Useful after form submission or cancel actions.
 */
export async function hideKeyboard(): Promise<void> {
  if (!isNativePlatform()) {
    // Web fallback: blur the active element
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    return
  }

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    await Keyboard.hide()
  } catch {
    // Fallback to blur
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
  }
}

/**
 * Show the keyboard programmatically (Android only — iOS ignores this).
 */
export async function showKeyboard(): Promise<void> {
  if (!isNativePlatform()) return

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    await Keyboard.show()
  } catch {
    // Non-critical
  }
}

/**
 * Set keyboard accessory bar visibility (iOS only).
 * The accessory bar is the bar with "Done" / navigation arrows
 * above the keyboard.
 *
 * @param visible - true to show, false to hide
 */
export async function setAccessoryBarVisible(visible: boolean): Promise<void> {
  if (!isNativePlatform()) return

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    await Keyboard.setAccessoryBarVisible({ isVisible: visible })
  } catch {
    // Non-critical, iOS only
  }
}

/**
 * Enable/disable automatic scroll when keyboard is shown.
 * On by default. Disable if you handle scrolling manually.
 */
export async function setScrollEnabled(enabled: boolean): Promise<void> {
  if (!isNativePlatform()) return

  try {
    const { Keyboard } = await import('@capacitor/keyboard')
    await Keyboard.setScroll({ isDisabled: !enabled })
  } catch {
    // Non-critical
  }
}
